import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  getSubmittedHoleInOneClaims,
  getHoleInOneClaimDetails,
  updateHoleInOneClaimStatus,
} from '@/api/holeInOneClaim/holeInOneClaim.actions';
import {
  HoleInOneClaim,
  UpdateHoleInOneClaimStatusData,
} from '@/api/holeInOneClaim/holeInOneClaim.types';
import { isApiSuccess } from '@/api/shared.types.ts';
import { useAsync } from '@/hooks/global/useAsync';
import { useHoleInOneClaimDetailsStore } from '@/stores/useHoleInOneClaimDetailsStore.ts';

const DEFAULT_PAGE_SIZE = 10;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useHoleInOneClaims = (pageSize = DEFAULT_PAGE_SIZE) => {
  const [holeInOneClaimsCount, setHoleInOneClaimsCount] = useState(0);
  const [holeInOneClaims, setHoleInOneClaims] = useState<HoleInOneClaim[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { isLoading } = useAsync();
  const { run: runFetchAll, isLoading: isFetchingAll } = useAsync();

  const fetchHoleInOneClaims = async (page = 1): Promise<void> => {
    const response = await runFetchAll(() =>
      getSubmittedHoleInOneClaims(page, pageSize)
    );

    if (isApiSuccess(response)) {
      setHoleInOneClaims(response.content.data);
      setHoleInOneClaimsCount(response.content.pagination.total);
      return;
    }

    toast.error(response.error.message);
    setHoleInOneClaims([]);
  };

  const fetchHoleInOneClaimDetails = async (id: string): Promise<void> => {
    const response = await runFetchAll(() => getHoleInOneClaimDetails(id));

    if (isApiSuccess(response)) {
      const { setHoleInOneClaimDetails } =
        useHoleInOneClaimDetailsStore.getState();
      setHoleInOneClaimDetails(response.content.claimDetails);
      return;
    }

    toast.error(response.error.message);
  };

  const updateClaimStatus = async (
    id: string,
    data: UpdateHoleInOneClaimStatusData
  ): Promise<void> => {
    const response = await updateHoleInOneClaimStatus(id, data);

    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }

    if (!response.content?.updatedHoleInOneClaim) {
      toast.error(
        'Failed to update status. Please refresh the page to view the latest changes and try again.'
      );
      return;
    }

    setHoleInOneClaims((prev) =>
      prev.map((claim) =>
        claim.id === response.content.updatedHoleInOneClaim.id
          ? {
              ...claim,
              status: response.content.updatedHoleInOneClaim.status,
            }
          : claim
      )
    );

    toast.success('Claim status updated successfully.');
  };

  useEffect(() => {
    fetchHoleInOneClaims(currentPage);
  }, [currentPage]);

  return {
    holeInOneClaimsCount,
    holeInOneClaims,
    fetchHoleInOneClaimDetails,
    updateClaimStatus,
    isLoading,
    isFetchingAll,
    pageSize,
    currentPage,
    setCurrentPage,
  };
};
